import { isTemplateOrContainsToken } from '../../../../Utils/tenantUrlTemplate';
import { sharedTenantResetUserConfig } from './mockTenantData';
import { mapOperationalStateToDisplay } from './adminTenantMappers';

export const PAGE_SIZE = 10;

export const defaultFilters = {
  status: '',
  label: '',
};

export const UI_STATUS_OPTIONS = [
  'Available',
  'Booked',
  'Faulted',
  'Pending Cleanup',
  'Maintenance',
  'Under Upgrade',
];

export const getTenantKey = (tenant) => `${tenant?.tenantType}-${tenant?.tenantNumber}`;

/** Same as list item `id` / getAdminTenantId — unique across tenant types. */
export const getTenantSelectionId = (tenant) => getTenantKey(tenant);

export const deriveSelectedTenants = (allTenants, selectedIds) => {
  if (!selectedIds?.size || !Array.isArray(allTenants)) return [];
  const byId = new Map(allTenants.map((tenant) => [tenant.id, tenant]));
  return [...selectedIds].map((id) => byId.get(id)).filter(Boolean);
};

export const getTenantDisplayName = (tenant) => `Tenant ${tenant?.tenantNumber}`;

export const hasActiveBooking = (tenant) =>
  tenant?.bookingStatus === 'Booked' && Boolean(tenant?.currentBooking);

export const isExpiredBooking = (tenant) => {
  const endTime = tenant?.currentBooking?.bookingEndTime;
  if (!endTime) return false;
  const endDate = new Date(endTime);
  return !Number.isNaN(endDate.getTime()) && endDate.getTime() < Date.now();
};

export const getNowIso = () => new Date().toISOString();

/** Release API expects YYYY-MM-DDTHH:mm:ss.sssZ (same shape Booking page sends). */
export const formatBookingEndTimeForRelease = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString();
};

export const stripUrlProtocol = (url) => {
  if (!url) return '';
  return String(url).replace(/^https?:\/\//i, '');
};

export const getDisplayUrl = (url) => stripUrlProtocol(url);

export const getHostName = (url) => {
  try {
    return new URL(url.startsWith('http') ? url : `https://${url}`).hostname;
  } catch (error) {
    return stripUrlProtocol(url);
  }
};

export const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatBookingDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatShortTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

export const formatRemainingTime = (endTime) => {
  if (!endTime) return '';
  const endDate = new Date(endTime);
  if (Number.isNaN(endDate.getTime())) return '';
  const diffMs = endDate.getTime() - Date.now();
  if (diffMs <= 0) return 'Expired';
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
};

export const getUniqueLabels = (tenants) =>
  [...new Set((tenants || []).flatMap((tenant) => tenant.labels || []))]
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

export const getUniqueTenantFeatures = (tenants) =>
  [...new Set((tenants || []).flatMap((tenant) => tenant.tenantFeatures || []))]
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

export const getMainTenantStatus = (tenant) => {
  if (!tenant) return 'Available';

  if (tenant.displayStatus) return tenant.displayStatus;

  if (tenant.operationalState != null && tenant.operationalState !== '') {
    return mapOperationalStateToDisplay(tenant.operationalState);
  }

  if (tenant?.isActive === false) return 'Inactive';
  if (tenant.cleanupStatus === 'Running') return 'Cleanup Running';
  if (tenant.cleanupStatus === 'Requested') return 'Pending Cleanup';
  if (tenant.cleanupStatus === 'Pending' || tenant.bookingStatus === 'Pending Cleanup') return 'Pending Cleanup';
  if (tenant.bookingStatus === 'Booked') return 'Booked';
  if (tenant.availabilityStatus === 'Under Upgrade') return 'Under Upgrade';
  if (tenant.availabilityStatus === 'Maintenance') return 'Maintenance';
  if (tenant.faultStatus === 'Faulted') return 'Faulted';
  return 'Available';
};

export const getStatusSupportingText = (tenant) => {
  const status = getMainTenantStatus(tenant);
  const booking = tenant?.currentBooking;
  const bookedBy = booking?.bookedUserName || booking?.bookedBy;

  if (status === 'Booked' && booking && bookedBy) {
    const until = formatShortTime(booking.bookingEndTime);
    return until ? `${bookedBy} · until ${until}` : bookedBy;
  }
  if (status === 'Faulted' && bookedBy) {
    return bookedBy;
  }
  if (status === 'Pending Cleanup') return 'Cleanup required';
  if (status === 'Cleanup Running') return 'Cleanup in progress';
  return '';
};

export const getRowIndicatorType = (tenant) => {
  const status = getMainTenantStatus(tenant);
  if (status === 'Faulted') return 'faulted';
  if (status === 'Pending Cleanup' || status === 'Cleanup Running') return 'cleanup';
  if (status === 'Under Upgrade') return 'upgrade';
  if (status === 'Maintenance') return 'maintenance';
  return '';
};

const STATUS_TRANSITIONS = {
  Available: ['Maintenance', 'Under Upgrade'],
  Maintenance: ['Available', 'Under Upgrade'],
  'Under Upgrade': ['Available', 'Maintenance'],
  Faulted: ['Maintenance', 'Under Upgrade'],
  'Pending Cleanup': ['Maintenance', 'Under Upgrade'],
};

export const getAvailableStatusTransitions = (tenant) => {
  const current = getMainTenantStatus(tenant);
  return STATUS_TRANSITIONS[current] || [];
};

export const getAllowedStatusTransitions = getAvailableStatusTransitions;

export const canChangeStatus = (tenant) => getAvailableStatusTransitions(tenant).length > 0;

export const canEditTenant = (tenant) =>
  tenant?.status === 'Maintenance' || tenant?.status === 'Upgrade';

export const getChangeStatusOptions = getAvailableStatusTransitions;

export const canDeleteTenant = (tenant) => {
  const status = getMainTenantStatus(tenant);
  if (['Pending Cleanup', 'Cleanup Running'].includes(status)) return false;
  if (status === 'Booked') {
    return tenant?.status === 'Maintenance' || tenant?.status === 'Upgrade';
  }
  return true;
};

export const canResetTenantPasswords = (tenant) => {
  const status = getMainTenantStatus(tenant);
  return !['Booked', 'Pending Cleanup', 'Cleanup Running'].includes(status);
};

export const canTriggerCleanup = (tenant) => {
  const status = getMainTenantStatus(tenant);
  return status === 'Faulted' || status === 'Pending Cleanup';
};

export const canMarkTenantFaulted = (tenant) => {
  const status = getMainTenantStatus(tenant);
  if (status !== 'Booked') return false;
  if (!tenant?.currentBooking) return false;
  const faultStatus = tenant.currentBooking.faultStatus || 'No';
  return faultStatus === 'No';
};

export const canClearTenantFault = (tenant) => {
  const status = getMainTenantStatus(tenant);
  if (status !== 'Faulted') return false;
  const faultStatus = tenant?.currentBooking?.faultStatus;
  return !faultStatus || faultStatus === 'Yes';
};

export const canReleaseTenant = (tenant) => {
  const status = getMainTenantStatus(tenant);
  if (status !== 'Booked' && status !== 'Faulted') return false;
  if (!tenant?.currentBooking?.bookingEndTime) return false;
  if (tenant.tenantNumber == null || !tenant.tenantType) return false;
  return true;
};

export const buildReleaseTenantPayload = (tenant) => ({
  bookingEndTime: formatBookingEndTimeForRelease(tenant.currentBooking.bookingEndTime),
  tenantNumber: tenant.tenantNumber,
  bookingTenantType: tenant.tenantType,
});

export const toBackendTenantStatus = (displayStatus) => {
  if (displayStatus === 'Under Upgrade') return 'Upgrade';
  if (displayStatus === 'Available') return null;
  if (displayStatus === 'Maintenance') return 'Maintenance';
  return displayStatus;
};

export const toDisplayTenantStatus = (rawStatus) => {
  if (rawStatus === 'Upgrade') return 'Under Upgrade';
  if (rawStatus == null || rawStatus === '') return 'Available';
  return rawStatus;
};

export const mapStatusToApi = toBackendTenantStatus;
export const mapStatusFromApi = toDisplayTenantStatus;

export const getStatusConfirmationMessage = (tenant, newStatus) => {
  const name = getTenantDisplayName(tenant);

  if (newStatus === 'Maintenance') {
    return {
      title: `Change ${name} to Maintenance?`,
      text: 'The tenant will be unavailable for booking until its status is changed back to Available.',
    };
  }
  if (newStatus === 'Under Upgrade') {
    return {
      title: `Change ${name} to Under Upgrade?`,
      text: 'The tenant will remain unavailable for booking while the upgrade is in progress.',
    };
  }
  if (newStatus === 'Available') {
    return {
      title: `Make ${name} available?`,
      text: 'This tenant will become available for booking again.',
    };
  }

  return {
    title: `Change ${name} to ${newStatus}?`,
    text: '',
  };
};

export const getStatusSuccessMessage = (tenant, newStatus) => {
  const name = getTenantDisplayName(tenant);
  if (newStatus === 'Available') return `${name} is now Available.`;
  if (newStatus === 'Maintenance') return `${name} moved to Maintenance.`;
  if (newStatus === 'Under Upgrade') return `${name} moved to Under Upgrade.`;
  return `${name} status updated.`;
};

export const labelsHaveChanges = (originalLabels, editedLabels) => {
  const original = normalizeLabels(originalLabels || []);
  const edited = normalizeLabels(editedLabels || []);
  if (original.length !== edited.length) return true;
  const originalSet = new Set(original);
  return edited.some((label) => !originalSet.has(label));
};

export const normalizeTenantFeatures = (features) =>
  [...new Set((features || []).map((feature) => String(feature).trim()).filter(Boolean))];

export const tenantFeaturesHaveChanges = (originalFeatures, editedFeatures) => {
  const original = normalizeTenantFeatures(originalFeatures || []);
  const edited = normalizeTenantFeatures(editedFeatures || []);
  if (original.length !== edited.length) return true;
  const originalLower = new Set(original.map((f) => f.toLowerCase()));
  return edited.some((feature) => !originalLower.has(feature.toLowerCase()));
};

export const diffTenantFeatures = (originalFeatures, editedFeatures) => {
  const original = normalizeTenantFeatures(originalFeatures || []);
  const edited = normalizeTenantFeatures(editedFeatures || []);
  const editedLower = new Set(edited.map((feature) => feature.toLowerCase()));
  const originalLower = new Set(original.map((feature) => feature.toLowerCase()));
  const add = edited.filter((feature) => !originalLower.has(feature.toLowerCase()));
  const remove = original.filter((feature) => !editedLower.has(feature.toLowerCase()));
  return { add, remove };
};

export const tenantFeaturesPayloadHasChanges = (payload) => (
  Object.values(payload || {}).some((group) => (
    (group || []).some((entry) => entry.add?.length > 0 || entry.remove?.length > 0)
  ))
);

export const buildTenantFeaturesPayload = (tenant, originalFeatures, editedFeatures) => {
  const { add, remove } = diffTenantFeatures(originalFeatures, editedFeatures);
  if (!add.length && !remove.length) return {};
  return {
    [tenant.tenantType]: [{
      tenantNumber: Number(tenant.tenantNumber),
      add,
      remove,
    }],
  };
};

export const TENANT_FEATURE_BULK_OPERATIONS = {
  ADD: 'add',
  REMOVE: 'remove',
  REPLACE: 'replace',
};

export const computeTenantFeaturesAfterOperation = (existingFeatures, operation, inputFeatures) => {
  const current = normalizeTenantFeatures(existingFeatures);
  const input = normalizeTenantFeatures(inputFeatures);

  if (operation === TENANT_FEATURE_BULK_OPERATIONS.ADD) {
    const seen = new Set(current.map((feature) => feature.toLowerCase()));
    const merged = [...current];
    input.forEach((feature) => {
      const key = feature.toLowerCase();
      if (!seen.has(key)) {
        merged.push(feature);
        seen.add(key);
      }
    });
    return normalizeTenantFeatures(merged);
  }

  if (operation === TENANT_FEATURE_BULK_OPERATIONS.REMOVE) {
    const removeKeys = new Set(input.map((feature) => feature.toLowerCase()));
    return current.filter((feature) => !removeKeys.has(feature.toLowerCase()));
  }

  if (operation === TENANT_FEATURE_BULK_OPERATIONS.REPLACE) {
    return input;
  }

  return current;
};

export const buildBulkTenantFeaturesPayload = (tenants, operation, inputFeatures) => {
  const payload = {};
  const input = normalizeTenantFeatures(inputFeatures);

  (tenants || []).forEach((tenant) => {
    if (!tenant?.tenantType || tenant.tenantNumber == null) return;

    let add = [];
    let remove = [];

    if (operation === TENANT_FEATURE_BULK_OPERATIONS.ADD) {
      add = input;
    } else if (operation === TENANT_FEATURE_BULK_OPERATIONS.REMOVE) {
      remove = input;
    } else if (operation === TENANT_FEATURE_BULK_OPERATIONS.REPLACE) {
      ({ add, remove } = diffTenantFeatures(tenant.tenantFeatures, input));
    }

    if (!add.length && !remove.length) return;

    if (!payload[tenant.tenantType]) payload[tenant.tenantType] = [];
    payload[tenant.tenantType].push({
      tenantNumber: Number(tenant.tenantNumber),
      add,
      remove,
    });
  });

  return payload;
};

export const summarizeBulkTenantFeatureTargets = (tenants) => (
  (tenants || []).reduce((acc, tenant) => {
    const type = tenant?.tenantType || 'Unknown';
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {})
);

export const normalizeVersionFeatureList = (features) => normalizeTenantFeatures(features);

export const versionFeatureListHaveChanges = (originalFeatures, editedFeatures) =>
  tenantFeaturesHaveChanges(originalFeatures, editedFeatures);

export const buildVersionFeaturesUpsertPayload = (entries) => ({
  entries: (entries || []).map((entry) => ({
    label: String(entry.label || '').trim(),
    versionFeatures: normalizeVersionFeatureList(entry.versionFeatures),
  })),
});

export const buildVersionFeaturesDeletePayload = (labels) => ({
  labels: [...new Set((labels || []).map((label) => String(label).trim()).filter(Boolean))],
});

export const buildStatusPayload = (tenant, newUiStatus) => ({
  status: toBackendTenantStatus(newUiStatus),
  tenantType: tenant.tenantType,
  tenantNumber: Number(tenant.tenantNumber),
});

export const buildFaultPayload = (tenant, markFaulted) => ({
  tenantNumber: tenant.tenantNumber,
  bookingTenantType: tenant.tenantType,
  currentFaultStatus: markFaulted ? 'No' : 'Yes',
  newFaultStatus: markFaulted ? 'Yes' : 'No',
});

export const buildLabelsPayload = (tenant, originalLabels, newLabels) => {
  const added = newLabels.filter((label) => !originalLabels.includes(label));
  const removed = originalLabels.filter((label) => !newLabels.includes(label));
  return {
    [tenant.tenantType]: [{
      tenantNumber: tenant.tenantNumber,
      add: added,
      remove: removed,
    }],
  };
};

export const buildOnboardTenantPayload = (form) => {
  const payload = {
    tenantType: form.tenantType,
    tenantUrl: normalizeUrl(form.tenantUrl),
    itsmUrl: normalizeUrl(form.itsmUrl),
    username: normalizeComparable(form.username),
    password: form.password,
    apiKey: form.apiKey,
    key: form.accessKey,
    secretKey: form.secretKey,
  };

  const optionalFields = [
    ['jiraUrl', form.jiraUrl, normalizeUrl],
    ['jiraUsername', form.jiraUsername, normalizeComparable],
    ['jiraPassword', form.jiraPassword, (value) => value],
    ['catchpointUrl', form.catchpointUrl, normalizeUrl],
    ['catchpointUsername', form.catchpointUsername, normalizeComparable],
    ['catchpointPassword', form.catchpointPassword, (value) => value],
    ['chatOpsUsername', form.chatopsUsername, normalizeComparable],
    ['chatOpsPassword', form.chatopsPassword, (value) => value],
    ['dynatraceHost', form.dynatraceHost, normalizeComparable],
    ['dynatraceUsername', form.dynatraceUsername, normalizeComparable],
    ['dynatracePassword', form.dynatracePassword, (value) => value],
  ];

  optionalFields.forEach(([key, value, normalizer]) => {
    const normalized = normalizer(value);
    if (normalizeComparable(normalized)) {
      payload[key] = normalized;
    }
  });

  const labels = normalizeLabels(
    String(form.labels || '')
      .split(',')
      .map((label) => label.trim())
      .filter(Boolean)
  );
  if (labels.length) payload.labels = labels;

  return payload;
};

export const buildOnboardPayload = buildOnboardTenantPayload;

export const getInitialOnboardForm = () => ({
  tenantType: 'Internal',
  tenantUrl: '',
  itsmUrl: '',
  itsmUrlManuallyEdited: false,
  username: '',
  password: '',
  apiKey: '',
  accessKey: '',
  secretKey: '',
  jiraUrl: '',
  jiraUsername: '',
  jiraPassword: '',
  catchpointUrl: '',
  catchpointUsername: '',
  catchpointPassword: '',
  chatopsUsername: '',
  chatopsPassword: '',
  dynatraceHost: '',
  dynatraceUsername: '',
  dynatracePassword: '',
  labels: '',
});

export const ONBOARD_STEPS = [
  { id: 1, label: 'Tenant Details' },
  { id: 2, label: 'Access & Integrations' },
  { id: 3, label: 'Review' },
];

export const detectItsmUrlFromTenantUrl = (tenantUrl) => {
  const host = getHostName(tenantUrl);
  if (!host) return { suggestedUrl: '', isDetected: false };

  const itomDemoPattern = /-itom-demo(\.|$)/i;
  if (!itomDemoPattern.test(host)) {
    return { suggestedUrl: '', isDetected: false };
  }

  const suggestedHost = host.replace(/-itom-demo/i, '-demo-restapi');
  if (suggestedHost === host) {
    return { suggestedUrl: '', isDetected: false };
  }

  return {
    suggestedUrl: `https://${suggestedHost}`,
    isDetected: true,
  };
};

export const validateOnboardStep1 = (form) => {
  const errors = {};
  if (!normalizeComparable(form.tenantUrl) || isTemplateOrContainsToken(form.tenantUrl)) {
    errors.tenantUrl = 'Required';
  } else if (!isValidUrl(form.tenantUrl)) {
    errors.tenantUrl = 'Enter a valid URL';
  }
  if (!normalizeComparable(form.itsmUrl) || isTemplateOrContainsToken(form.itsmUrl)) {
    errors.itsmUrl = 'Required';
  } else if (!isValidUrl(form.itsmUrl)) {
    errors.itsmUrl = 'Enter a valid URL';
  }
  return errors;
};

export const validateOnboardStep2 = (form) => {
  const errors = {};
  ['username', 'password', 'apiKey', 'accessKey', 'secretKey'].forEach((field) => {
    if (!normalizeComparable(form[field])) errors[field] = 'Required';
  });
  if (normalizeComparable(form.jiraUrl) && !isValidUrl(form.jiraUrl)) {
    errors.jiraUrl = 'Enter a valid URL';
  }
  if (normalizeComparable(form.catchpointUrl) && !isValidUrl(form.catchpointUrl)) {
    errors.catchpointUrl = 'Enter a valid URL';
  }
  return errors;
};

export const buildDeleteTenantPayload = (tenant) => ({
  tenantUrl: tenant.tenantUrl,
  tenantType: tenant.tenantType,
});

export const buildDeletePayload = buildDeleteTenantPayload;

export const TENANT_ITSM_URL_UNAVAILABLE_MESSAGE = 'ITSM URL is not available for this tenant.';

export const resolveTenantItsmUrl = (tenant) => {
  const direct = normalizeComparable(tenant?.itsmUrl);
  if (direct) return direct;

  const { suggestedUrl } = detectItsmUrlFromTenantUrl(tenant?.tenantUrl || '');
  return normalizeComparable(suggestedUrl);
};

export const enrichTenantWithItsmUrl = (tenant) => {
  if (!tenant) return tenant;

  const itsmUrl = resolveTenantItsmUrl(tenant);
  if (!itsmUrl || itsmUrl === normalizeComparable(tenant.itsmUrl)) {
    return tenant;
  }

  return { ...tenant, itsmUrl };
};

export const buildResetPasswordPayload = (tenant, selectedUsers, authCredentials) => ({
  itsmUrl: resolveTenantItsmUrl(tenant),
  userList: selectedUsers,
  username: authCredentials.username,
  password: authCredentials.password,
});

export const buildResetPasswordsPayload = buildResetPasswordPayload;

export const getPasswordResetResultSummary = (result) => {
  const successfulResets = result?.successfulResets || [];
  const failedResets = result?.failedResets || [];
  const invalidUsers = result?.invalidUsers || [];

  return {
    successfulCount: successfulResets.length,
    failedCount: failedResets.length,
    invalidCount: invalidUsers.length,
    successfulResets,
    failedResets,
    invalidUsers,
    isFullSuccess: failedResets.length === 0 && invalidUsers.length === 0 && successfulResets.length > 0,
    isTotalFailure: successfulResets.length === 0 && (failedResets.length > 0 || invalidUsers.length > 0),
    hasPartialIssues: (failedResets.length > 0 || invalidUsers.length > 0) && successfulResets.length > 0,
  };
};

export const parseCleanupErrorEntry = (entry) => {
  const text = String(entry || '').trim();
  if (!text) return { label: 'Unknown tenant', reason: 'Cleanup failed.' };
  const separatorIndex = text.indexOf(':');
  if (separatorIndex === -1) return { label: text, reason: 'Cleanup failed.' };
  return {
    label: text.slice(0, separatorIndex).trim(),
    reason: text.slice(separatorIndex + 1).trim() || 'Cleanup failed.',
  };
};

/**
 * Liam appears in the shared tenant user list and also names the privileged credential
 * family used to authorize resets. Those are separate concepts — the authorization
 * username entered by Admin is not assumed to match the "Liam" list entry.
 * Disable Liam account reset here if backend confirms it must not be resettable.
 */
export const LIAM_ACCOUNT_RESET_ENABLED = true;

export const isSharedTenantUserResetAllowed = (userConfig, { liamResetEnabled = LIAM_ACCOUNT_RESET_ENABLED } = {}) => {
  if (!userConfig?.resetAllowed) return false;
  if (userConfig.name === 'Liam' && !liamResetEnabled) return false;
  return true;
};

export const getResetPasswordUserOptions = ({ liamResetEnabled = LIAM_ACCOUNT_RESET_ENABLED } = {}) =>
  sharedTenantResetUserConfig.filter((user) => isSharedTenantUserResetAllowed(user, { liamResetEnabled }));

export const filterResetPasswordUsersBySearch = (users, searchTerm) => {
  const normalized = String(searchTerm || '').trim().toLowerCase();
  if (!normalized) return users;
  return users.filter(({ name }) => name.toLowerCase().includes(normalized));
};

export const getTenantCredentials = (tenant) => {
  const stored = tenant?.credentials || {};
  const fromApi = Boolean(tenant?.hasDetail);

  if (fromApi) {
    return {
      password: stored.password || '',
      apiKey: stored.apiKey || '',
      accessKey: stored.accessKey || '',
      secretKey: stored.secretKey || '',
      jira: {
        url: stored.jira?.url || '',
        username: stored.jira?.username || '',
        password: stored.jira?.password || '',
      },
      catchpoint: {
        url: stored.catchpoint?.url || '',
        username: stored.catchpoint?.username || '',
        password: stored.catchpoint?.password || '',
      },
      chatops: {
        username: stored.chatops?.username || '',
        password: stored.chatops?.password || '',
      },
      dynatrace: {
        host: stored.dynatrace?.host || '',
        username: stored.dynatrace?.username || '',
        password: stored.dynatrace?.password || '',
      },
    };
  }

  return {
    password: stored.password || `${getTenantKey(tenant)}-dummy-password`,
    apiKey: stored.apiKey || `${tenant?.id}-api-key`,
    accessKey: stored.accessKey || `${tenant?.id}-access-key`,
    secretKey: stored.secretKey || `${tenant?.id}-secret-key`,
    jira: {
      url: stored.jira?.url || tenant?.integrations?.jira || '',
      username: stored.jira?.username || 'demodesign@google.com',
      password: stored.jira?.password || `${tenant?.id}-jira-password`,
    },
    catchpoint: {
      url: stored.catchpoint?.url || tenant?.integrations?.catchpoint || '',
      username: stored.catchpoint?.username || 'catchpoint_service_user',
      password: stored.catchpoint?.password || `${tenant?.id}-catchpoint-password`,
    },
    chatops: {
      username: stored.chatops?.username || 'tenant_ops_bot',
      password: stored.chatops?.password || `${tenant?.id}-chatops-token`,
    },
    dynatrace: {
      host: stored.dynatrace?.host || tenant?.integrations?.dynatrace || '',
      username: stored.dynatrace?.username || 'dynatrace_service_user',
      password: stored.dynatrace?.password || `${tenant?.id}-dynatrace-password`,
    },
  };
};

export const buildTenantEditDraft = (tenant) => {
  const creds = getTenantCredentials(tenant);
  return {
    tenantUrl: tenant?.tenantUrl || '',
    itsmUrl: tenant?.itsmUrl || '',
    username: tenant?.username || '',
    password: '',
    apiKey: '',
    accessKey: '',
    secretKey: '',
    jiraUrl: creds.jira.url || '',
    jiraUsername: creds.jira.username || '',
    jiraPassword: '',
    catchpointUrl: creds.catchpoint.url || '',
    catchpointUsername: creds.catchpoint.username || '',
    catchpointPassword: '',
    chatopsUsername: creds.chatops.username || '',
    chatopsPassword: '',
    dynatraceHost: creds.dynatrace.host || '',
    dynatraceUsername: creds.dynatrace.username || '',
    dynatracePassword: '',
  };
};

const SECRET_FIELD_KEYS = [
  'password',
  'apiKey',
  'accessKey',
  'secretKey',
  'jiraPassword',
  'catchpointPassword',
  'chatopsPassword',
  'dynatracePassword',
];

const normalizeComparable = (value) => String(value || '').trim();

export const isTenantIntegrationEditable = (tenant, integrationKey) => {
  if (!tenant?.hasDetail) return false;
  return Boolean(tenant?.integrationConfigured?.[integrationKey]);
};

export const isFieldChanged = (originalTenant, draft, field, editingSecrets = {}) => {
  const creds = getTenantCredentials(originalTenant);

  if (SECRET_FIELD_KEYS.includes(field)) {
    return Boolean(editingSecrets[field]);
  }

  const comparisons = {
    tenantUrl: () => normalizeComparable(draft.tenantUrl) !== normalizeComparable(originalTenant.tenantUrl),
    itsmUrl: () => normalizeComparable(draft.itsmUrl) !== normalizeComparable(originalTenant.itsmUrl),
    username: () => normalizeComparable(draft.username) !== normalizeComparable(originalTenant.username),
    jiraUrl: () => normalizeComparable(draft.jiraUrl) !== normalizeComparable(creds.jira.url),
    jiraUsername: () => normalizeComparable(draft.jiraUsername) !== normalizeComparable(creds.jira.username),
    catchpointUrl: () => normalizeComparable(draft.catchpointUrl) !== normalizeComparable(creds.catchpoint.url),
    catchpointUsername: () => normalizeComparable(draft.catchpointUsername) !== normalizeComparable(creds.catchpoint.username),
    chatopsUsername: () => normalizeComparable(draft.chatopsUsername) !== normalizeComparable(creds.chatops.username),
    dynatraceHost: () => normalizeComparable(draft.dynatraceHost) !== normalizeComparable(creds.dynatrace.host),
    dynatraceUsername: () => normalizeComparable(draft.dynatraceUsername) !== normalizeComparable(creds.dynatrace.username),
  };

  return comparisons[field] ? comparisons[field]() : false;
};

const INTEGRATION_EDIT_FIELDS = [
  { field: 'jiraUrl', integration: 'jira' },
  { field: 'jiraUsername', integration: 'jira' },
  { field: 'jiraPassword', integration: 'jira' },
  { field: 'catchpointUrl', integration: 'catchpoint' },
  { field: 'catchpointUsername', integration: 'catchpoint' },
  { field: 'catchpointPassword', integration: 'catchpoint' },
  { field: 'chatopsUsername', integration: 'chatops' },
  { field: 'chatopsPassword', integration: 'chatops' },
  { field: 'dynatraceHost', integration: 'dynatrace' },
  { field: 'dynatraceUsername', integration: 'dynatrace' },
  { field: 'dynatracePassword', integration: 'dynatrace' },
];

export const isTenantEditDirty = (originalTenant, draft, editingSecrets = {}) => {
  const fields = [
    'tenantUrl',
    'itsmUrl',
    'username',
    ...SECRET_FIELD_KEYS,
  ];

  INTEGRATION_EDIT_FIELDS.forEach(({ field, integration }) => {
    if (isTenantIntegrationEditable(originalTenant, integration)) {
      fields.push(field);
    }
  });

  return fields.some((field) => isFieldChanged(originalTenant, draft, field, editingSecrets));
};

export const buildTenantUpdatePayload = (originalTenant, draft, editingSecrets = {}) => {
  const payload = {};

  if (isFieldChanged(originalTenant, draft, 'tenantUrl')) {
    payload.tenantUrl = normalizeUrl(draft.tenantUrl);
  }
  if (isFieldChanged(originalTenant, draft, 'itsmUrl')) {
    payload.itsmUrl = normalizeUrl(draft.itsmUrl);
  }
  if (isFieldChanged(originalTenant, draft, 'username')) {
    payload.username = normalizeComparable(draft.username);
  }
  if (editingSecrets.password && normalizeComparable(draft.password)) {
    payload.password = normalizeComparable(draft.password);
  }
  if (editingSecrets.apiKey && normalizeComparable(draft.apiKey)) {
    payload.apiKey = normalizeComparable(draft.apiKey);
  }
  if (editingSecrets.accessKey && normalizeComparable(draft.accessKey)) {
    payload.key = normalizeComparable(draft.accessKey);
  }
  if (editingSecrets.secretKey && normalizeComparable(draft.secretKey)) {
    payload.secretKey = normalizeComparable(draft.secretKey);
  }

  if (isTenantIntegrationEditable(originalTenant, 'jira')) {
    if (isFieldChanged(originalTenant, draft, 'jiraUrl')) {
      payload.jiraUrl = normalizeUrl(draft.jiraUrl);
    }
    if (isFieldChanged(originalTenant, draft, 'jiraUsername')) {
      payload.jiraUsername = normalizeComparable(draft.jiraUsername);
    }
    if (editingSecrets.jiraPassword && normalizeComparable(draft.jiraPassword)) {
      payload.jiraPassword = normalizeComparable(draft.jiraPassword);
    }
  }

  if (isTenantIntegrationEditable(originalTenant, 'catchpoint')) {
    if (isFieldChanged(originalTenant, draft, 'catchpointUrl')) {
      payload.catchpointUrl = normalizeUrl(draft.catchpointUrl);
    }
    if (isFieldChanged(originalTenant, draft, 'catchpointUsername')) {
      payload.catchpointUsername = normalizeComparable(draft.catchpointUsername);
    }
    if (editingSecrets.catchpointPassword && normalizeComparable(draft.catchpointPassword)) {
      payload.catchpointPassword = normalizeComparable(draft.catchpointPassword);
    }
  }

  if (isTenantIntegrationEditable(originalTenant, 'chatops')) {
    if (isFieldChanged(originalTenant, draft, 'chatopsUsername')) {
      payload.chatopsUsername = normalizeComparable(draft.chatopsUsername);
    }
    if (editingSecrets.chatopsPassword && normalizeComparable(draft.chatopsPassword)) {
      payload.chatopsPassword = normalizeComparable(draft.chatopsPassword);
    }
  }

  if (isTenantIntegrationEditable(originalTenant, 'dynatrace')) {
    if (isFieldChanged(originalTenant, draft, 'dynatraceHost')) {
      payload.dynatraceHost = normalizeComparable(draft.dynatraceHost);
    }
    if (isFieldChanged(originalTenant, draft, 'dynatraceUsername')) {
      payload.dynatraceUsername = normalizeComparable(draft.dynatraceUsername);
    }
    if (editingSecrets.dynatracePassword && normalizeComparable(draft.dynatracePassword)) {
      payload.dynatracePassword = normalizeComparable(draft.dynatracePassword);
    }
  }

  return payload;
};

export const buildTenantEditPayload = (originalTenant, draft, editingSecrets = {}) => {
  const changes = buildTenantUpdatePayload(originalTenant, draft, editingSecrets);
  if (Object.keys(changes).length === 0) return null;

  return {
    tenantNumber: Number(originalTenant.tenantNumber),
    tenantType: originalTenant.tenantType,
    status: originalTenant.status,
    ...changes,
  };
};

export const validateTenantEditDraft = (originalTenant, draft, editingSecrets = {}) => {
  const errors = {};
  const payload = buildTenantUpdatePayload(originalTenant, draft, editingSecrets);

  if (payload.tenantUrl !== undefined) {
    if (!normalizeComparable(draft.tenantUrl)) errors.tenantUrl = 'Required';
    else if (!isValidUrl(draft.tenantUrl)) errors.tenantUrl = 'Enter a valid URL';
  }
  if (payload.itsmUrl !== undefined) {
    if (!normalizeComparable(draft.itsmUrl)) errors.itsmUrl = 'Required';
    else if (!isValidUrl(draft.itsmUrl)) errors.itsmUrl = 'Enter a valid URL';
  }
  if (payload.username !== undefined && !normalizeComparable(draft.username)) {
    errors.username = 'Required';
  }
  if (payload.jiraUrl !== undefined && draft.jiraUrl.trim() && !isValidUrl(draft.jiraUrl)) {
    errors.jiraUrl = 'Enter a valid URL';
  }
  if (payload.catchpointUrl !== undefined && draft.catchpointUrl.trim() && !isValidUrl(draft.catchpointUrl)) {
    errors.catchpointUrl = 'Enter a valid URL';
  }

  SECRET_FIELD_KEYS.forEach((field) => {
    if (editingSecrets[field] && !normalizeComparable(draft[field])) {
      errors[field] = 'Enter a new value or cancel the change';
    }
  });

  return errors;
};

export const suggestItsmUrl = (tenantUrl) => detectItsmUrlFromTenantUrl(tenantUrl).suggestedUrl;

export const matchesTenantSearch = (tenant, query) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;
  const searchableValues = [
    tenant.tenantNumber,
    tenant.tenantUrl,
    stripUrlProtocol(tenant.tenantUrl),
    ...(tenant.labels || []),
    tenant.currentBooking?.bookedUserName,
    tenant.currentBooking?.bookedBy,
    tenant.currentBooking?.bookedUserEmail,
  ];
  return searchableValues.some((value) =>
    String(value || '').toLowerCase().includes(normalizedQuery)
  );
};

export const isValidUrl = (value) => {
  if (!value) return false;
  try {
    const url = new URL(value.startsWith('http') ? value : `https://${value}`);
    return ['http:', 'https:'].includes(url.protocol);
  } catch (error) {
    return false;
  }
};

export const normalizeUrl = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  return trimmed.startsWith('http') ? trimmed : `https://${trimmed}`;
};

export const normalizeLabels = (labels) =>
  [...new Set((labels || []).map((label) => String(label).trim()).filter(Boolean))];

export const getTenantActions = (tenant) => {
  const status = getMainTenantStatus(tenant);
  const actions = [{ key: 'view', label: 'View Details' }];

  const addIf = (condition, action) => {
    if (condition) actions.push(action);
  };

  if (status === 'Available') {
    addIf(canEditTenant(tenant), { key: 'edit', label: 'Edit Tenant' });
    addIf(true, { key: 'labels', label: 'Manage Labels' });
    addIf(canChangeStatus(tenant), { key: 'status', label: 'Change Status' });
    addIf(canResetTenantPasswords(tenant), { key: 'resetPasswords', label: 'Reset Shared Tenant Passwords' });
    addIf(canDeleteTenant(tenant), { key: 'delete', label: 'Delete Tenant', destructive: true });
  } else if (status === 'Booked') {
    addIf(true, { key: 'labels', label: 'Manage Labels' });
    addIf(canMarkTenantFaulted(tenant), { key: 'fault', label: 'Mark as Faulted' });
    addIf(canReleaseTenant(tenant), { key: 'release', label: 'Release Tenant' });
  } else if (status === 'Faulted') {
    addIf(true, { key: 'labels', label: 'Manage Labels' });
    addIf(canClearTenantFault(tenant), { key: 'unfault', label: 'Clear Fault' });
    addIf(canChangeStatus(tenant), { key: 'status', label: 'Change Status' });
    addIf(canReleaseTenant(tenant), { key: 'release', label: 'Release Tenant' });
    addIf(canResetTenantPasswords(tenant), { key: 'resetPasswords', label: 'Reset Shared Tenant Passwords' });
    addIf(canDeleteTenant(tenant), { key: 'delete', label: 'Delete Tenant', destructive: true });
  } else if (status === 'Pending Cleanup') {
    addIf(true, { key: 'labels', label: 'Manage Labels' });
    addIf(canChangeStatus(tenant), { key: 'status', label: 'Change Status' });
  } else if (status === 'Cleanup Running') {
    addIf(true, { key: 'labels', label: 'Manage Labels' });
  } else if (status === 'Maintenance') {
    addIf(canEditTenant(tenant), { key: 'edit', label: 'Edit Tenant' });
    addIf(true, { key: 'labels', label: 'Manage Labels' });
    addIf(canChangeStatus(tenant), { key: 'status', label: 'Change Status' });
    addIf(canResetTenantPasswords(tenant), { key: 'resetPasswords', label: 'Reset Shared Tenant Passwords' });
    addIf(canDeleteTenant(tenant), { key: 'delete', label: 'Delete Tenant', destructive: true });
  } else if (status === 'Under Upgrade') {
    addIf(canEditTenant(tenant), { key: 'edit', label: 'Edit Tenant' });
    addIf(true, { key: 'labels', label: 'Manage Labels' });
    addIf(canChangeStatus(tenant), { key: 'status', label: 'Change Status' });
    addIf(canResetTenantPasswords(tenant), { key: 'resetPasswords', label: 'Reset Shared Tenant Passwords' });
    addIf(canDeleteTenant(tenant), { key: 'delete', label: 'Delete Tenant', destructive: true });
  } else if (status === 'Inactive') {
    addIf(canEditTenant(tenant), { key: 'edit', label: 'Edit Tenant' });
    addIf(true, { key: 'labels', label: 'Manage Labels' });
    addIf(canDeleteTenant(tenant), { key: 'delete', label: 'Delete Tenant', destructive: true });
  }

  return actions;
};

export const getDrawerOperationalActions = (tenant) =>
  getTenantActions(tenant).filter((action) => !['view', 'edit'].includes(action.key));

export const downloadTenantCsv = (tenants) => {
  const columns = [
    ['Tenant Number', 'tenantNumber'],
    ['Tenant Type', 'tenantType'],
    ['Tenant URL', 'tenantUrl'],
    ['ITSM URL', 'itsmUrl'],
    ['Labels', 'labels'],
    ['Status', 'status'],
    ['Operational State', 'operationalState'],
    ['Booked By', 'bookedBy'],
    ['Booking End', 'bookingEnd'],
  ];

  const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const rows = [
    columns.map(([label]) => escapeCsv(label)).join(','),
    ...(tenants || []).map((tenant) => columns.map(([, key]) => {
      if (key === 'labels') return escapeCsv((tenant.labels || []).join('; '));
      if (key === 'status') return escapeCsv(tenant.status ?? '');
      if (key === 'operationalState') return escapeCsv(getMainTenantStatus(tenant));
      if (key === 'bookedBy') {
        return escapeCsv(tenant.currentBooking?.bookedUserName || tenant.currentBooking?.bookedBy || '');
      }
      if (key === 'bookingEnd') {
        return escapeCsv(formatBookingDate(tenant.currentBooking?.bookingEndTime));
      }
      return escapeCsv(tenant[key]);
    }).join(',')),
  ];

  const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'shared-tenant-management-export.csv');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
